import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  BookOpen, 
  Users, 
  Calendar, 
  Library, 
  Feather,
  Play,
  Download,
  Sparkles
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Welcome Onboarding (shown after registration)
export const WelcomeOnboarding = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);
  const { user } = useAuth();

  const steps = [
    {
      title: "Welcome to the Society",
      subtitle: `Dear ${user?.name?.split(' ')[0] || 'Friend'},`,
      content: "You've just joined a community of thoughtful letter writers who believe in the power of intentional correspondence. Let us show you around.",
      icon: Feather,
      highlight: "lws-green"
    },
    {
      title: "Your Letter Writing Journey",
      subtitle: "What awaits you",
      content: "Explore our courses to master the art of correspondence, connect with pen pals around the world, and access exclusive resources to elevate your writing.",
      features: [
        { icon: BookOpen, label: "Courses", desc: "Learn the art of letter writing" },
        { icon: Users, label: "Pen Pals", desc: "Find meaningful connections" },
        { icon: Library, label: "Resources", desc: "Templates, guides & stationery" },
        { icon: Calendar, label: "Events", desc: "Join chapter gatherings" }
      ],
      highlight: "lws-amber"
    },
    {
      title: "Start with Module 1",
      subtitle: "Free for all members",
      content: "Begin your journey with 'The Philosophy of Presence' — our introductory module that explores why letter writing is the ultimate act of intentionality.",
      cta: "Begin Your Journey",
      ctaHref: "#courses",
      icon: Sparkles,
      highlight: "lws-green"
    }
  ];

  const currentStep = steps[step];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-lws-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="welcome-onboarding"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-lws-paper max-w-lg w-full rounded-sm shadow-2xl overflow-hidden"
      >
        {/* Progress indicator */}
        <div className="flex gap-1 p-4 pb-0">
          {steps.map((stepItem, i) => (
            <div
              key={`progress-step-${i}`}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-lws-green' : 'bg-lws-ink/10'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Icon */}
              {currentStep.icon && (
                <div className={`w-16 h-16 rounded-full bg-${currentStep.highlight}/10 flex items-center justify-center mb-6 mx-auto`}>
                  <currentStep.icon className={`w-8 h-8 text-${currentStep.highlight}`} />
                </div>
              )}

              {/* Subtitle */}
              {currentStep.subtitle && (
                <p className="text-sm uppercase tracking-[0.2em] text-lws-taupe text-center mb-2">
                  {currentStep.subtitle}
                </p>
              )}

              {/* Title */}
              <h2 className="text-2xl font-serif text-lws-ink text-center mb-4">
                {currentStep.title}
              </h2>

              {/* Content */}
              <p className="text-lws-taupe text-center leading-relaxed mb-6">
                {currentStep.content}
              </p>

              {/* Features grid (step 2) */}
              {currentStep.features && (
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {currentStep.features.map((feature) => (
                    <div
                      key={feature.label}
                      className="p-4 bg-white border border-lws-ink/10 rounded-sm text-center"
                    >
                      <feature.icon className="w-5 h-5 text-lws-green mx-auto mb-2" />
                      <p className="font-medium text-lws-ink text-sm">{feature.label}</p>
                      <p className="text-xs text-lws-taupe mt-1">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA button (final step) */}
              {currentStep.cta && (
                <a
                  href={currentStep.ctaHref}
                  onClick={onComplete}
                  className="block w-full bg-lws-green text-white text-center py-4 text-sm uppercase tracking-wider hover:bg-lws-green/90 transition-colors"
                  data-testid="onboarding-cta"
                >
                  {currentStep.cta}
                </a>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="px-8 pb-8 flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-sm text-lws-taupe hover:text-lws-ink transition-colors"
            data-testid="onboarding-skip"
          >
            Skip for now
          </button>

          <div className="flex gap-2">
            {step > 0 && (
              <button
                onClick={handlePrev}
                className="p-2 border border-lws-ink/20 text-lws-taupe hover:border-lws-green hover:text-lws-green transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {step < steps.length - 1 && (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-lws-ink text-white text-sm uppercase tracking-wider hover:bg-lws-ink/90 transition-colors flex items-center gap-2"
                data-testid="onboarding-next"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Courses Page Tooltip Tour
export const CoursesTooltipTour = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const tooltipSteps = [
    {
      target: '[data-testid="module-module-1"]',
      title: "Your First Free Module",
      content: "This is 'The Philosophy of Presence' — completely free for all members. Click to expand and see the lessons inside.",
      placement: "bottom"
    },
    {
      target: '[data-testid="lesson-1-1"]',
      title: "Video Lessons",
      content: "Click on any video lesson to watch it. You'll see a play icon and 'Click to watch' for video content.",
      placement: "bottom",
      expandModule: true
    },
    {
      target: '[data-testid="complete-btn-1-1"]',
      title: "Track Your Progress",
      content: "Mark lessons as complete to track your journey. Your progress is saved automatically.",
      placement: "left",
      expandModule: true
    },
    {
      target: '[data-testid="resource-library-link"]',
      title: "Resource Library",
      content: "Access downloadable guides, templates, and stationery in the Resource Library. Premium resources unlock as you upgrade your membership.",
      placement: "top",
      scrollTo: true
    }
  ];

  const currentTooltip = tooltipSteps[step];

  // Position tooltip near target element
  const updatePosition = useCallback(() => {
    const target = document.querySelector(currentTooltip.target);
    if (target) {
      const rect = target.getBoundingClientRect();
      const scrollY = window.scrollY;
      
      let top, left;
      
      switch (currentTooltip.placement) {
        case 'bottom':
          top = rect.bottom + scrollY + 12;
          left = rect.left + rect.width / 2;
          break;
        case 'top':
          top = rect.top + scrollY - 12;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + scrollY + rect.height / 2;
          left = rect.left - 12;
          break;
        case 'right':
          top = rect.top + scrollY + rect.height / 2;
          left = rect.right + 12;
          break;
        default:
          top = rect.bottom + scrollY + 12;
          left = rect.left + rect.width / 2;
      }
      
      setPosition({ top, left, rect });
    }
  }, [currentTooltip]);

  useEffect(() => {
    // Expand module 1 if needed
    if (currentTooltip.expandModule) {
      const moduleToggle = document.querySelector('[data-testid="module-toggle-module-1"]');
      const moduleContent = document.querySelector('[data-testid="module-module-1"]');
      if (moduleToggle && moduleContent) {
        // Check if module is collapsed (no lesson visible)
        const lessonVisible = document.querySelector('[data-testid="lesson-1-1"]');
        if (!lessonVisible) {
          moduleToggle.click();
        }
      }
    }

    // Scroll to element if needed
    if (currentTooltip.scrollTo) {
      const target = document.querySelector(currentTooltip.target);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // Wait for DOM to settle then position
    const timer = setTimeout(updatePosition, 300);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [step, currentTooltip, updatePosition]);

  const handleNext = () => {
    if (step < tooltipSteps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Check if target exists
  const targetExists = document.querySelector(currentTooltip.target);

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-lws-ink/40 z-40"
        onClick={onSkip}
      />

      {/* Spotlight on target */}
      {targetExists && position.rect && (
        <div
          className="fixed z-40 rounded-sm ring-4 ring-lws-green/50 pointer-events-none"
          style={{
            top: position.rect.top - 4,
            left: position.rect.left - 4,
            width: position.rect.width + 8,
            height: position.rect.height + 8,
          }}
        />
      )}

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="fixed z-50 w-80 bg-lws-paper shadow-xl rounded-sm border border-lws-green/20"
          style={{
            top: currentTooltip.placement === 'top' ? position.top - 180 : position.top,
            left: Math.max(20, Math.min(position.left - 160, window.innerWidth - 340)),
          }}
          data-testid="courses-tooltip"
        >
          {/* Arrow */}
          <div 
            className={`absolute w-3 h-3 bg-lws-paper border-lws-green/20 transform rotate-45 ${
              currentTooltip.placement === 'bottom' ? '-top-1.5 border-l border-t' :
              currentTooltip.placement === 'top' ? '-bottom-1.5 border-r border-b' :
              currentTooltip.placement === 'left' ? '-right-1.5 border-t border-r' :
              '-left-1.5 border-b border-l'
            }`}
            style={{ left: '50%', marginLeft: '-6px' }}
          />

          <div className="p-5">
            {/* Step indicator */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-lws-green font-medium">
                Step {step + 1} of {tooltipSteps.length}
              </span>
              <button
                onClick={onSkip}
                className="text-lws-taupe hover:text-lws-ink"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <h3 className="font-serif text-lg text-lws-ink mb-2">
              {currentTooltip.title}
            </h3>
            <p className="text-sm text-lws-taupe leading-relaxed mb-4">
              {currentTooltip.content}
            </p>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={onSkip}
                className="text-xs text-lws-taupe hover:text-lws-ink"
                data-testid="tooltip-skip"
              >
                Skip tour
              </button>
              <div className="flex gap-2">
                {step > 0 && (
                  <button
                    onClick={handlePrev}
                    className="px-3 py-1.5 text-xs border border-lws-ink/20 text-lws-taupe hover:border-lws-green hover:text-lws-green transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="px-4 py-1.5 text-xs bg-lws-green text-white hover:bg-lws-green/90 transition-colors"
                  data-testid="tooltip-next"
                >
                  {step === tooltipSteps.length - 1 ? "Finish" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

// Hook to manage onboarding state
export const useOnboarding = () => {
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [showCoursesTour, setShowCoursesTour] = useState(false);

  useEffect(() => {
    if (user) {
      // Check if welcome onboarding was completed
      const welcomeCompleted = localStorage.getItem(`lws_onboarding_welcome_${user.email}`);
      if (!welcomeCompleted) {
        // Show welcome after a short delay
        const timer = setTimeout(() => setShowWelcome(true), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  const completeWelcome = () => {
    if (user) {
      localStorage.setItem(`lws_onboarding_welcome_${user.email}`, 'completed');
    }
    setShowWelcome(false);
  };

  const skipWelcome = () => {
    if (user) {
      localStorage.setItem(`lws_onboarding_welcome_${user.email}`, 'skipped');
    }
    setShowWelcome(false);
  };

  const startCoursesTour = () => {
    if (user) {
      const coursesTourCompleted = localStorage.getItem(`lws_onboarding_courses_${user.email}`);
      if (!coursesTourCompleted) {
        setShowCoursesTour(true);
      }
    }
  };

  const completeCoursesTour = () => {
    if (user) {
      localStorage.setItem(`lws_onboarding_courses_${user.email}`, 'completed');
    }
    setShowCoursesTour(false);
  };

  const skipCoursesTour = () => {
    if (user) {
      localStorage.setItem(`lws_onboarding_courses_${user.email}`, 'skipped');
    }
    setShowCoursesTour(false);
  };

  const resetOnboarding = () => {
    if (user) {
      localStorage.removeItem(`lws_onboarding_welcome_${user.email}`);
      localStorage.removeItem(`lws_onboarding_courses_${user.email}`);
    }
  };

  return {
    showWelcome,
    showCoursesTour,
    completeWelcome,
    skipWelcome,
    startCoursesTour,
    completeCoursesTour,
    skipCoursesTour,
    resetOnboarding
  };
};

export default WelcomeOnboarding;
